/**
 * @module ol-ishare/http
 */

// The normalize and urlJoin function are copied from
// https://github.com/jfromaniello/url-join/blob/main/lib/url-join.js (MIT
// License)

/**
 * Appends query parameters to a URI.
 *
 * @param {String} uri The original URI, which may already have query data.
 * @param {!Object} params An object where keys are URI-encoded parameter keys,
 *     and the values are arbitrary types or arrays.
 * @return {String} The new URI.
 */
function appendParams(uri, params) {
  var keyParams = [];
  // Skip any null or undefined parameter values
  Object.keys(params).forEach(function (k) {
    if (params[k] !== null && params[k] !== undefined) {
      keyParams.push(k + '=' + encodeURIComponent(params[k]));
    }
  });
  var qs = keyParams.join('&');
  // remove any trailing ? or &
  uri = uri.replace(/[?&]$/, '');
  // append ? or & depending on whether uri has existing parameters
  uri = uri.indexOf('?') === -1 ? uri + '?' : uri + '&';
  return uri + qs;
}

/**
 * Checks HTTP status of fetch response, if OK return response else throw an Error with details
 * @package
 * @param {Response} response Response instance returned by fetch
 * @returns {Response} Response instance with OK HTTP status
 * @throws {Error} Error instance with `address` (URL), `errno` (HTTP status) and `info` (HTTP status text)
 */
function checkHttpStatusIsOk(response) {
  if (response.ok) {
    return response;
  } else {
    var err = new Error('Request rejected with status ' + response.status);
    err.address = response.url;
    err.errno = response.status;
    err.info = response.statusText;
    throw err;
  }
}

/**
 * Normalize URL parts
 * @param {Array<String>} strArray List of parts to join into a valid URL
 * @returns {String} Joined URL
 */
function normalize(strArray) {
  const resultArray = [];
  if (strArray.length === 0) {
    return '';
  }

  // Filter out any empty string values.
  strArray = strArray.filter((part) => part !== '');

  if (typeof strArray[0] !== 'string') {
    throw new TypeError('Url must be a string. Received ' + strArray[0]);
  }

  // If the first part is a plain protocol, we combine it with the next part.
  if (strArray[0].match(/^[^/:]+:\/*$/) && strArray.length > 1) {
    strArray[0] = strArray.shift() + strArray[0];
  }

  // If the first part is a leading slash, we combine it with the next part.
  if (strArray[0] === '/' && strArray.length > 1) {
    strArray[0] = strArray.shift() + strArray[0];
  }

  // There must be two or three slashes in the file protocol, two slashes in anything else.
  if (strArray[0].match(/^file:\/\/\//)) {
    strArray[0] = strArray[0].replace(/^([^/:]+):\/*/, '$1:///');
  } else if (!strArray[0].match(/^\[.*:.*\]/)) {
    // If the first part is not an IPv6 host, we replace the protocol.
    strArray[0] = strArray[0].replace(/^([^/:]+):\/*/, '$1://');
  }

  for (let i = 0; i < strArray.length; i++) {
    let component = strArray[i];

    if (typeof component !== 'string') {
      throw new TypeError('Url must be a string. Received ' + component);
    }

    if (i > 0) {
      // Removing the starting slashes for each component but the first.
      component = component.replace(/^[\/]+/, '');
    }
    if (i < strArray.length - 1) {
      // Removing the ending slashes for each component but the last.
      component = component.replace(/[\/]+$/, '');
    } else {
      // For the last component we will combine multiple slashes to a single one.
      component = component.replace(/[\/]+$/, '/');
    }

    if (component === '') {
      continue;
    }

    resultArray.push(component);
  }

  let str = '';

  for (let i = 0; i < resultArray.length; i++) {
    const part = resultArray[i];

    // Do not add a slash if this is the first part.
    if (i === 0) {
      str += part;
      continue;
    }

    const prevPart = resultArray[i - 1];

    // Do not add a slash if the previous part ends with start of the query param or hash.
    if ((prevPart && prevPart.endsWith('?')) || prevPart.endsWith('#')) {
      str += part;
      continue;
    }

    str += '/' + part;
  }
  // Each input component is now separated by a single slash except the possible first plain protocol part.

  // remove trailing slash before parameters or hash
  str = str.replace(/\/(\?|&|#[^!])/g, '$1');

  // replace ? and & in parameters with &
  const [beforeHash, afterHash] = str.split('#');
  const parts = beforeHash.split(/(?:\?|&)+/).filter(Boolean);
  str =
    parts.shift() +
    (parts.length > 0 ? '?' : '') +
    parts.join('&') +
    (afterHash && afterHash.length > 0 ? '#' + afterHash : '');

  return str;
}

/**
 * Join parts of a URL into a valid URL
 * @param {...String} args Parts of the URL to join
 * @returns {String} Joined URL
 */
function urlJoin(...args) {
  const parts = Array.from(Array.isArray(args[0]) ? args[0] : args);
  return normalize(parts);
}

/**
 * Create a URL instance
 * @param {string} baseUrl Base URL
 * @param {string} path Path to append to baseUrl
 * @param {object} qs Object containing properties that will be appended to searchParams
 * @returns {URL} URL instance
 */
function buildUrl(baseUrl = '', path = '', qs = {}) {
  const url = new URL(baseUrl);
  // Append to the path preserving existing path
  url.pathname = urlJoin(url.pathname, path);
  // Append query string parameters
  for (const [key, value] of Object.entries(qs)) {
    // console.log(`${key}: ${value}`);
    url.searchParams.append(key, value);
  }
  return url;
}

export { appendParams, buildUrl, checkHttpStatusIsOk, urlJoin };
